import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import UPButton from 'components/button';
import UPInput from 'components/input';
import {
  MFA_ENROLLMENT__CODE_LABEL,
  MFA_ENROLLMENT__CONTINUE,
  MFA_ENROLLMENT__RETRY_IN_SECONDS,
  MFA_ENROLLMENT__SECRET_COPIED,
  MFA_ENROLLMENT__SETUP_INSTRUCTIONS,
  MFA_ENROLLMENT__SETUP_TITLE,
} from 'translations/constants';
import IMfaEnrollmentPayload from 'types/auth/IMfaEnrollmentPayload';
import { MfaFailure, isCompleteCode, normaliseCode, translateMfaFailure } from '../mfa-helpers';
import { MfaButtonRow, MfaCardTitle, MfaErrorSlot, MfaHint, MfaInstructions } from '../mfa-styles';
import { MfaQrFrame, MfaSecret } from './styles';

interface MfaSetupViewProps {
  payload: IMfaEnrollmentPayload;
  submitting: boolean;
  failure: MfaFailure;
  retrySeconds: number;
  onSubmit: (code: string) => void;
  onCancel: () => void;
  /**
   * Translation key for the secondary button. The two entry points leave the user somewhere
   * different: enrolling at login goes back to the sign-in form, while the grace-period opt-in modal
   * just closes and drops the user back on the page they were already on.
   */
  cancelLabelKey: string;
  onCodeEdited: () => void;
}

const QR_SIZE = 168;

const MfaSetupView = (props: MfaSetupViewProps) => {
  const { payload, submitting, failure, retrySeconds, onSubmit, onCancel, cancelLabelKey, onCodeEdited } = props;
  const [t] = useTranslation();
  const [code, setCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  const handleCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCode(normaliseCode(event.target.value));
      // Clear the previous failure as soon as the user starts fixing it, so a stale "that code isn't
      // correct" doesn't sit under a field they have already changed.
      onCodeEdited();
    },
    [onCodeEdited],
  );

  const copySecret = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(payload.secret);
      setSecretCopied(true);
    } catch {
      // Clipboard access can be denied. The secret is on screen and selectable either way, so there
      // is nothing useful to tell the user here.
    }
  }, [payload.secret]);

  const waiting = retrySeconds > 0;
  const canSubmit = !submitting && !waiting && isCompleteCode(code);

  return (
    <>
      <MfaCardTitle>{t(MFA_ENROLLMENT__SETUP_TITLE)}</MfaCardTitle>
      <MfaQrFrame>
        <QRCodeSVG value={payload.otpAuthUri} size={QR_SIZE} />
      </MfaQrFrame>
      {/* Standalone, never interpolated into a sentence, so the instruction key carries no placeholder. */}
      <MfaSecret onClick={copySecret}>{payload.secret}</MfaSecret>
      {secretCopied && <MfaHint>{t(MFA_ENROLLMENT__SECRET_COPIED)}</MfaHint>}
      <MfaInstructions>{t(MFA_ENROLLMENT__SETUP_INSTRUCTIONS)}</MfaInstructions>
      {/* No maxLength: it caps the *raw* value in the DOM before onChange runs, so pasting "123a456"
          would arrive here already truncated to "123a45" and normalise to five digits. The controlled
          value plus normaliseCode enforces the six-digit cap instead. */}
      <UPInput
        value={code}
        onChange={handleCodeChange}
        label={t(MFA_ENROLLMENT__CODE_LABEL)}
        placeholder={t(MFA_ENROLLMENT__CODE_LABEL)}
        error={!!failure}
        inputMode="numeric"
        autoComplete="one-time-code"
      />
      <MfaErrorSlot>{failure && translateMfaFailure(t, failure)}</MfaErrorSlot>
      <MfaButtonRow>
        <UPButton text={t(cancelLabelKey)} onClick={onCancel} outlined />
        <UPButton
          text={waiting ? t(MFA_ENROLLMENT__RETRY_IN_SECONDS, { seconds: retrySeconds }) : t(MFA_ENROLLMENT__CONTINUE)}
          onClick={() => onSubmit(code)}
          disabled={!canSubmit}
        />
      </MfaButtonRow>
    </>
  );
};

export default MfaSetupView;
